import React, { useEffect, useState } from 'react';
import { Trophy, Medal, TrendingUp, Volume2, Maximize2, VolumeX, Minimize2 } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { sellers } from '../utils/mockData';
import { formatCurrency } from '../utils/format';
import { EvolutionChart } from '../components/ranking/EvolutionChart';
export function Ranking() {
  const {
    addToast
  } = useToast();
  const [period, setPeriod] = useState('Mensal');
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [isTVMode, setIsTVMode] = useState(false);
  // Sort sellers by sales count for demo
  const sortedSellers = [...sellers].sort((a, b) => b.salesCount - a.salesCount);
  const top3 = sortedSellers.slice(0, 3);
  const restOfSellers = sortedSellers.slice(3);
  // Split remaining sellers into two columns
  const midPoint = Math.ceil(restOfSellers.length / 2);
  const leftColumnSellers = restOfSellers.slice(0, midPoint);
  const rightColumnSellers = restOfSellers.slice(midPoint);
  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
    addToast(soundEnabled ? 'Sons de notificação desativados' : 'Sons de notificação ativados', 'info');
  };
  const toggleTVMode = () => {
    if (!isTVMode) {
      document.documentElement.requestFullscreen().catch(e => {
        console.error(e);
      });
      addToast('Modo TV ativado - Pressione ESC para sair', 'success');
    } else {
      document.exitFullscreen().catch(e => {
        console.error(e);
      });
    }
    setIsTVMode(!isTVMode);
  };
  // Listen for fullscreen change (ESC key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsTVMode(false);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);
  const Content = () => <div className={`space-y-6 ${isTVMode ? 'p-8 bg-background min-h-screen' : ''}`} data-id="element-538">
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-539">
        <div className="flex bg-slate-800 p-1 rounded-lg" data-id="element-540">
          {['Mensal', 'Trimestral', 'Anual'].map(p => <button key={p} onClick={() => setPeriod(p)} className={`
                px-4 py-1.5 rounded-md text-sm font-medium transition-all
                ${period === p ? 'bg-primary text-white shadow-lg' : 'text-text-muted hover:text-white'}
              `} data-id="element-541">
              {p}
            </button>)}
        </div>

        <div className="flex gap-2" data-id="element-542">
          <Button variant={soundEnabled ? 'primary' : 'ghost'} size="sm" onClick={toggleSound} icon={soundEnabled ? <Volume2 className="h-4 w-4" data-id="element-544" /> : <VolumeX className="h-4 w-4" data-id="element-545" />} data-id="element-543">
            Sons
          </Button>
          <Button variant={isTVMode ? 'primary' : 'ghost'} size="sm" onClick={toggleTVMode} icon={isTVMode ? <Minimize2 className="h-4 w-4" data-id="element-547" /> : <Maximize2 className="h-4 w-4" data-id="element-548" />} data-id="element-546">
            {isTVMode ? 'Sair TV' : 'Modo TV'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-549">
        {/* Podium Section (Left) - Compact Design */}
        <div className="lg:col-span-1 flex flex-col" data-id="element-550">
          <div className="relative h-[400px] bg-gradient-to-b from-slate-800/50 to-slate-900/50 rounded-2xl border border-white/5 p-4 flex items-end justify-center gap-2 overflow-hidden" data-id="element-551">
            {/* Confetti Effect Background (Simulated) */}
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" data-id="element-552" />

            {/* 2nd Place */}
            <div className="flex flex-col items-center z-10 animate-in slide-in-from-bottom-10 duration-700 delay-100 mb-4" data-id="element-553">
              <Avatar src={top3[1].avatar} fallback={top3[1].name} size="lg" className="mb-2 ring-4 ring-slate-300" data-id="element-554" />
              <div className="flex flex-col items-center bg-slate-800/80 backdrop-blur-sm p-3 rounded-t-xl w-20 h-28 border-t-4 border-slate-300 shadow-lg" data-id="element-555">
                <Medal className="h-5 w-5 text-slate-300 mb-1" data-id="element-556" />
                <span className="text-xl font-bold text-white" data-id="element-557">2</span>
                <span className="text-[10px] text-center font-medium mt-auto truncate w-full" data-id="element-558">
                  {top3[1].name.split(' ')[0]}
                </span>
                <span className="text-[9px] text-text-muted" data-id="element-559">
                  {top3[1].salesCount}
                </span>
              </div>
            </div>

            {/* 1st Place */}
            <div className="flex flex-col items-center z-20 animate-in slide-in-from-bottom-10 duration-700" data-id="element-560">
              <div className="relative" data-id="element-561">
                <Trophy className="absolute -top-6 left-1/2 -translate-x-1/2 h-6 w-6 text-yellow-400 animate-bounce" data-id="element-562" />
                <Avatar src={top3[0].avatar} fallback={top3[0].name} size="xl" className="mb-2 ring-4 ring-yellow-400 shadow-lg shadow-yellow-400/20" data-id="element-563" />
              </div>
              <div className="flex flex-col items-center bg-gradient-to-b from-yellow-500/20 to-slate-800/80 backdrop-blur-sm p-3 rounded-t-xl w-24 h-40 border-t-4 border-yellow-400 shadow-xl" data-id="element-564">
                <Medal className="h-6 w-6 text-yellow-400 mb-1" data-id="element-565" />
                <span className="text-3xl font-bold text-white" data-id="element-566">1</span>
                <span className="text-xs text-center font-bold mt-auto truncate w-full text-yellow-200" data-id="element-567">
                  {top3[0].name.split(' ')[0]}
                </span>
                <span className="text-[10px] text-yellow-100/80" data-id="element-568">
                  {top3[0].salesCount} vendas
                </span>
              </div>
            </div>

            {/* 3rd Place */}
            <div className="flex flex-col items-center z-10 animate-in slide-in-from-bottom-10 duration-700 delay-200 mb-2" data-id="element-569">
              <Avatar src={top3[2].avatar} fallback={top3[2].name} size="lg" className="mb-2 ring-4 ring-amber-700" data-id="element-570" />
              <div className="flex flex-col items-center bg-slate-800/80 backdrop-blur-sm p-3 rounded-t-xl w-20 h-20 border-t-4 border-amber-700 shadow-lg" data-id="element-571">
                <Medal className="h-5 w-5 text-amber-700 mb-1" data-id="element-572" />
                <span className="text-xl font-bold text-white" data-id="element-573">3</span>
                <span className="text-[10px] text-center font-medium mt-auto truncate w-full" data-id="element-574">
                  {top3[2].name.split(' ')[0]}
                </span>
                <span className="text-[9px] text-text-muted" data-id="element-575">
                  {top3[2].salesCount}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* List Section (Right) - 2 Columns */}
        <div className="lg:col-span-2 flex flex-col h-[400px]" data-id="element-576">
          <div className="flex items-center justify-between mb-2" data-id="element-577">
            <h2 className="text-xl font-bold text-white" data-id="element-578">Ranking Geral</h2>
            <div className="flex items-center gap-2 text-xs text-emerald-400 animate-pulse" data-id="element-579">
              <span className="h-2 w-2 rounded-full bg-emerald-500" data-id="element-580" />
              Atualizado em tempo real
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full overflow-hidden" data-id="element-581">
            {/* Left Column (Pos 4-8 approx) */}
            <Card className="divide-y divide-slate-700/50 overflow-y-auto custom-scrollbar" data-id="element-582">
              {leftColumnSellers.map((seller, index) => <RankingRow key={seller.id} seller={seller} rank={index + 4} topSales={top3[0].salesCount} data-id="element-583" />)}
            </Card>

            {/* Right Column (Pos 9-12 approx) */}
            <Card className="divide-y divide-slate-700/50 overflow-y-auto custom-scrollbar" data-id="element-584">
              {rightColumnSellers.map((seller, index) => <RankingRow key={seller.id} seller={seller} rank={index + 4 + leftColumnSellers.length} topSales={top3[0].salesCount} data-id="element-585" />)}
            </Card>
          </div>
        </div>
      </div>

      {/* Evolution Chart Section */}
      <div className="animate-in fade-in slide-in-from-bottom-4 duration-700" data-id="element-586">
        <EvolutionChart period={period} data-id="element-587" />
      </div>
    </div>;
  if (isTVMode) {
    return <Content data-id="element-588" />;
  }
  return <Layout data-id="element-589">
      <Content data-id="element-590" />
    </Layout>;
}
// Helper component for ranking rows to avoid duplication
function RankingRow({
  seller,
  rank,
  topSales
}: {
  seller: any;
  rank: number;
  topSales: number;
}) {
  return <div className="p-3 flex items-center gap-3 hover:bg-slate-800/50 transition-colors" data-id="element-591">
      <span className="w-6 text-center font-bold text-sm text-text-muted" data-id="element-592">
        {rank}º
      </span>

      <Avatar src={seller.avatar} fallback={seller.name} size="sm" data-id="element-593" />

      <div className="flex-1 min-w-0" data-id="element-594">
        <div className="flex justify-between mb-1" data-id="element-595">
          <span className="font-medium text-white text-sm truncate" data-id="element-596">
            {seller.name}
          </span>
          <span className="font-bold text-primary-light text-xs" data-id="element-597">
            {formatCurrency(seller.revenue)}
          </span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-1 overflow-hidden" data-id="element-598">
          <div className="bg-gradient-to-r from-primary to-secondary h-full rounded-full transition-all duration-1000" style={{
          width: `${seller.salesCount / topSales * 100}%`
        }} data-id="element-599" />
        </div>
      </div>

      <div className="text-right min-w-[50px]" data-id="element-600">
        <div className="text-sm font-bold text-white" data-id="element-601">{seller.salesCount}</div>
      </div>

      <div className={`
        flex items-center text-[10px] font-medium w-8 justify-end
        ${(seller.rankingChange || 0) > 0 ? 'text-emerald-400' : (seller.rankingChange || 0) < 0 ? 'text-red-400' : 'text-text-muted'}
      `} data-id="element-602">
        {(seller.rankingChange || 0) !== 0 && <TrendingUp className={`h-2.5 w-2.5 mr-0.5 ${(seller.rankingChange || 0) < 0 ? 'rotate-180' : ''}`} data-id="element-603" />}
        {Math.abs(seller.rankingChange || 0) > 0 ? Math.abs(seller.rankingChange || 0) : '-'}
      </div>
    </div>;
}